import React, { useRef } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';
import { ArrowRight, Github, ExternalLink } from 'lucide-react';
import { track } from '../lib/analytics';

const ProjectsSection = () => {
  const sectionRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: sectionRef,
    offset: ["start end", "end start"],
  });

  const featuredProjects = [
    {
      id: 1,
      title: 'Tally',
      subtitle: 'Chrome Extension',
      description: 'A quiet token meter for Claude: real-time usage, per-project budgets, and model-aware messages-left. 70K+ downloads on the Chrome Web Store.',
      tags: ['Claude', 'Chrome Extension', 'Tools'],
      link: 'https://chromewebstore.google.com/detail/tally-%E2%80%94-claude-token-coun/baicaaiepddopbodaccgfffdimceidbp',
      color: '#2563EB',
      image: '/images/tally.png',
    },
    {
      id: 2,
      title: 'wait.money',
      subtitle: 'Claude Code Tool',
      description: 'Get paid for the time you spend waiting on Claude Code to respond. The thinking spinner turns into a tiny ad, and founding members keep up to 100% of what advertisers pay.',
      tags: ['Claude Code', 'CLI', 'Monetization'],
      link: 'https://wait.money/',
      color: '#2F855A',
      image: '/images/waitmoney.png',
    },
    {
      id: 3,
      title: 'BrewClaw',
      subtitle: 'Open Source',
      description: 'Fastest-growing open-source tool making AI accessible to non-technical users. OpenClaw brings discovery and setup to everyone.',
      tags: ['Open Source', 'Next.js', 'AI'],
      github: 'https://github.com/kairothq/brewclaw',
      link: 'https://www.brewclaw.com',
      color: 'var(--accent-orange)',
      image: '/images/brewclaw.png',
    },
    {
      id: 4,
      title: 'Mindlessly',
      subtitle: 'Chrome Extension',
      description: 'Intention-setting before browsing with timer-based nudges. 2,695 active users with 8.2/10 satisfaction rating.',
      tags: ['Chrome Extension', 'Productivity', 'Shipped'],
      github: 'https://github.com/kairothq/Mindlessly',
      link: 'https://chromewebstore.google.com/detail/mindlessly/famahpafcdlfpoofjelaiobinfkjkfgl',
      color: 'var(--accent-green)',
      image: '/images/mindlessly.png',
    },
    {
      id: 5,
      title: 'X Sort',
      subtitle: 'Chrome Extension',
      description: 'Chrome extension that ranks any X.com profile by likes, views, or retweets in a sidebar. Spot viral outliers, export your swipe file. No API key, no logins.',
      tags: ['Chrome Extension', 'X.com', 'Productivity'],
      github: 'https://github.com/kairothq/x-sort-extension',
      link: 'https://xsort.vercel.app',
      color: '#14171A',
      image: '/images/xsort.png',
    },
    {
      id: 6,
      title: '50 Shades of AI',
      subtitle: 'Newsletter',
      description: 'LinkedIn newsletter breaking down AI tools for non-tech professionals. 2,500+ readers across published episodes.',
      tags: ['Content', 'AI', 'Newsletter'],
      link: 'https://divykairoth.substack.com',
      color: 'var(--accent-orange)',
      image: '/images/50shades.png',
    },
    {
      id: 7,
      title: 'Instagram',
      subtitle: '50K+ Followers',
      description: 'Building a personal brand around AI, products, and building in public. 50K+ followers with consistent content across reels, stories, and carousels.',
      tags: ['Content', 'Personal Brand', '50K+'],
      link: 'https://www.instagram.com/divy.kairoth/',
      color: '#E4405F',
      image: '/images/instagram.png',
    },
  ];

  return (
    <section 
      ref={sectionRef}
      id="projects" 
      className="relative py-32 px-6 main"
      style={{ background: 'white', zIndex: 50 }}
    >
      <div className="max-w-7xl mx-auto">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="mb-20 text-center"
        >
          <p 
            className="text-sm font-medium tracking-wide uppercase mb-4"
            style={{ color: 'var(--accent-orange)' }}
          >
            Featured Work
          </p>
          <h2 className="text-5xl md:text-6xl font-bold mb-6">
            <span style={{ color: '#122F26' }}>Selected </span>
            <span style={{ 
              background: 'linear-gradient(to right, var(--accent-green), var(--accent-orange))',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text',
            }}>Projects</span>
          </h2>
          <p 
            className="text-xl max-w-3xl mx-auto"
            style={{ color: '#122F26', opacity: 0.7 }}
          >
            A collection of products and campaigns I've built from 0 to 1
          </p>
        </motion.div>

        {/* Stacking Project Cards */}
        <div className="relative space-y-8">
          {featuredProjects.map((project, index) => {
            // Stacking logic: later projects come above earlier ones (increasing z-index)
            const baseZIndex = 60 + index;
            
            // Calculate individual card scroll progress
            const cardY = useTransform(
              scrollYProgress,
              [0.1 + index * 0.15, 0.4 + index * 0.15],
              [100, 0]
            );
            
            const cardScale = useTransform(
              scrollYProgress,
              [0.1 + index * 0.15, 0.3 + index * 0.15],
              [0.95, 1]
            );

            return (
              <motion.div
                key={project.id}
                style={{ 
                  y: cardY,
                  scale: cardScale,
                  position: 'sticky',
                  top: `${80 + index * 10}px`,
                  zIndex: baseZIndex,
                }}
                className="group project-card"
              >
                <div 
                  className="p-6 md:p-12 rounded-[2.5rem] backdrop-blur-md shadow-xl"
                  style={{
                    background: 'white',
                    border: `3px solid ${project.color}`,
                  }}
                >
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
                    {/* Left: Content */}
                    <div className="space-y-6">
                      <div>
                        <p 
                          className="text-sm font-medium mb-2"
                          style={{ color: project.color }}
                        >
                          {project.subtitle}
                        </p>
                        <h3 
                          className="text-4xl md:text-5xl font-bold mb-4"
                          style={{ color: '#122F26' }}
                        >
                          {project.title}
                        </h3>
                        <p 
                          className="text-lg leading-relaxed"
                          style={{ color: '#122F26', opacity: 0.8 }}
                        >
                          {project.description}
                        </p>
                      </div>

                      {/* Tags */}
                      <div className="flex flex-wrap gap-2">
                        {project.tags.map((tag, i) => (
                          <span
                            key={i}
                            className="px-4 py-2 rounded-full text-sm font-medium"
                            style={{
                              background: `${project.color}20`,
                              color: project.color,
                            }}
                          >
                            {tag}
                          </span>
                        ))}
                      </div>

                      {/* Links */}
                      <div className="flex items-center space-x-4 pt-4">
                        {project.github && (
                          <motion.a
                            href={project.github}
                            onClick={() => track('project_click', { project: project.title, target: 'github' })}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-center space-x-2 px-6 py-3 rounded-full"
                            style={{ background: 'rgba(18, 47, 38, 0.1)', color: '#122F26' }}
                            whileHover={{ scale: 1.05, background: project.color, color: 'white' }}
                            whileTap={{ scale: 0.95 }}
                          >
                            <Github size={20} />
                            <span>View Code</span>
                          </motion.a>
                        )}
                        <motion.a
                          href={project.link}
                          onClick={() => track('project_click', { project: project.title, target: 'learn_more' })}
                          className="flex items-center space-x-2 px-6 py-3 rounded-full"
                          style={{ background: project.color, color: 'white' }}
                          whileHover={{ scale: 1.05 }}
                          whileTap={{ scale: 0.95 }}
                        >
                          <span>Learn More</span>
                          <ArrowRight size={20} />
                        </motion.a>
                      </div>
                    </div>

                    {/* Right: Project Visual */}
                    <motion.div
                      whileHover={{ scale: 1.02 }}
                      className="relative aspect-video rounded-2xl overflow-hidden"
                      style={{
                        background: project.image ? '#000' : `linear-gradient(135deg, ${project.color}30 0%, ${project.color}10 100%)`,
                      }}
                    >
                      {project.image ? (
                        <img src={project.image} alt={project.title} className="w-full h-full object-contain" loading="lazy" />
                      ) : (
                        <div className="absolute inset-0 flex items-center justify-center">
                          <ExternalLink size={48} style={{ color: project.color, opacity: 0.3 }} />
                        </div>
                      )}
                    </motion.div>
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>

        {/* View All Projects CTA */}
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="text-center mt-20"
        >
          <motion.a
            href="/projects"
            onClick={() => track('nav_click', { location: 'projects_section', target: '/projects' })}
            className="inline-flex items-center space-x-3 px-12 py-6 rounded-full text-xl font-semibold"
            style={{ background: 'var(--accent-orange)', color: 'white' }}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            <span>View All Projects</span>
            <ArrowRight size={24} />
          </motion.a>
        </motion.div>
      </div>
    </section>
  );
};

export default ProjectsSection;
